import { openDialog } from "./Application";
import { t } from "./i18n";
import { antiforgeryHeaders } from "./Csrf";

function getUiLanguage() {
    return (document.documentElement.lang || "es").toLowerCase();
}

function createTopUpActionError(message) {
    const language = getUiLanguage();
    const error = new Error(message);
    error.actionUrl = `/${language}/payment?balance=true`;
    error.actionText = t("Top up balance");
    return error;
}

function createEmailCaptureError(communityId, storyUrl) {
    const error = new Error(t("You need an account to continue."));
    error.emailCapture = { community: communityId, url: storyUrl };
    return error;
}

async function isAuthenticated(communityId) {
    const fallback = document.body?.dataset?.authenticated === "true";

    try {
        const response = await fetch(`/api/user/status?community=${encodeURIComponent(Number.isFinite(communityId) ? communityId : 0)}`, {
            method: "GET",
            credentials: "same-origin",
            headers: {
                "Accept": "application/json",
                "Cache-Control": "no-cache"
            }
        });

        if (!response.ok) {
            return false;
        }

        const payload = await response.json();
        if (typeof payload?.authenticated === "boolean") {
            return payload.authenticated;
        }
    } catch {
    }

    return fallback;
}

async function buyStory(storyId, communityId, storyUrl) {
    const response = await fetch("/api/story-access/buy", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            ...antiforgeryHeaders()
        },
        credentials: "same-origin",
        body: JSON.stringify({ storyId, communityId })
    });

    if (!response.ok) {
        let body = null;
        try {
            body = await response.json();
        } catch {
        }
        const message = body?.message || t("This story could not be purchased.");

        if (body?.requiredCredits != null) {
            throw createTopUpActionError(message);
        }

        // No session: capture the email to send the signup link and
        // resume the purchase after topping up balance.
        if (response.status === 401) {
            throw createEmailCaptureError(communityId, storyUrl);
        }

        // Generic failure with an active session: offer the balance top-up.
        if (!body?.message) {
            throw createTopUpActionError(message);
        }

        throw new Error(message);
    }

    return response.json();
}

function updateCommunityBalance(communityId, communityBalance) {
    if (!Number.isFinite(communityBalance)) {
        return;
    }

    const selectors = [
        ".community-balance-value",
        Number.isFinite(communityId)
            ? `.community-balance-value[data-community-id=\"${communityId}\"]`
            : null
    ].filter(Boolean);

    const elements = document.querySelectorAll(selectors.join(","));
    elements.forEach((element) => {
        element.textContent = `$${communityBalance.toFixed(2)}`;
    });
}

async function onStoryBuyClick(event, trigger) {
    event.preventDefault();

    const storyId = Number(trigger.dataset.storyId);
    const communityId = Number(trigger.dataset.communityId);
    const storyUrl = trigger.dataset.storyUrl;
    const storyTitle = trigger.dataset.storyTitle;
    const storyPrice = Number(trigger.dataset.storyPrice);

    if (!storyId || !storyUrl || !storyTitle) {
        return;
    }

    // Visitor without a session: ask for the email directly to send them the signup
    // link, without showing the charge confirmation (they have no balance or
    // account to charge yet).
    if (!(await isAuthenticated(communityId))) {
        openDialog(
            "email",
            {
                community: Number.isFinite(communityId) ? communityId : null,
                url: storyUrl
            },
            t("Create your account to continue"),
            "small"
        );
        return;
    }

    openDialog(
        "warning",
        {
            title: storyTitle,
            credits: Number.isFinite(storyPrice) ? storyPrice : 0,
            onConfirm: async () => {
                const result = await buyStory(storyId, Number.isFinite(communityId) ? communityId : null, storyUrl);
                updateCommunityBalance(communityId, Number(result?.communityBalance));
                window.location.href = storyUrl;
            }
        },
        t("Warning"),
        "small"
    );
}

function initCommunityPurchaseButtons() {
    document.addEventListener("click", (event) => {
        const button = event.target.closest(".story-buy-button");
        if (button) {
            onStoryBuyClick(event, button);
        }
    });
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initCommunityPurchaseButtons);
} else {
    initCommunityPurchaseButtons();
}
